// @ts-nocheck
import { useEffect, useState } from "react";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ChevronDown } from "lucide-react";
import { SCHEDULING_PERKS, DEFAULT_TIER_PERKS, perkLabels } from "@/lib/schedulingPerks";

/** Collapsible tier card — collapsed by default. */
function TierCard({
  title,
  tone,
  summary,
  children,
}: {
  title: string;
  tone: string;
  summary?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-xl border ${tone}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
      >
        <div>
          <p className="font-semibold text-foreground text-base">{title}</p>
          {summary && <p className="text-xs text-muted-foreground mt-0.5">{summary}</p>}
        </div>
        <ChevronDown
          className={`w-5 h-5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-4 pb-4 space-y-4">{children}</div>}
    </div>
  );
}

interface Props {
  event: any;
  updateEvent: (patch: any) => void;
}

const TIERS = [
  { tierKey: "priority", perkField: "priority_tier_perks", descField: "priority_tier_description" },
  { tierKey: "club_priority", perkField: "club_priority_tier_perks", descField: "club_priority_tier_description" },
  { tierKey: "showcase", perkField: "showcase_tier_perks", descField: "showcase_tier_description" },
];

// Build the auto description (bullet list) from a set of perk keys.
const buildDesc = (keys: string[]): string =>
  perkLabels(keys).map((l) => `• ${l}`).join("\n");

export function SchedulingSection({ event, updateEvent }: Props) {
  const enabled = !!event.scheduling_tiers_enabled;

  // Seed prechecked defaults into the event the moment tiers are enabled, so
  // the selections are actually persisted (and flow to scheduling/constraints)
  // rather than just shown visually. Only fills tiers the admin hasn't touched.
  useEffect(() => {
    if (!enabled) return;
    const patch: any = {};
    for (const { tierKey, perkField, descField } of TIERS) {
      const val = event[perkField];
      if (!Array.isArray(val)) {
        const defaults = DEFAULT_TIER_PERKS[tierKey] ?? [];
        patch[perkField] = defaults;
        if (!event[descField]) patch[descField] = buildDesc(defaults);
      }
    }
    if (Object.keys(patch).length) updateEvent(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const numericPatch = (field: string, raw: string, opts?: { integer?: boolean; min?: number }) => {
    const cleaned = raw.replace(opts?.integer ? /[^0-9]/g : /[^0-9.]/g, "");
    let num = opts?.integer ? parseInt(cleaned, 10) : parseFloat(cleaned);
    if (!isFinite(num)) num = 0;
    if (opts?.min !== undefined && num < opts.min) num = opts.min;
    updateEvent({ [field]: opts?.integer ? num : Math.round(num * 100) / 100 });
  };

  // Effective perk selection: fall back to sensible defaults when the field
  // hasn't been initialized yet (the seeding effect normally handles this).
  const effectivePerks = (field: string, tierKey?: string): string[] => {
    const val = event[field];
    if (Array.isArray(val)) return val;
    return tierKey ? DEFAULT_TIER_PERKS[tierKey] ?? [] : [];
  };

  const PerkPicker = ({
    field,
    tierKey,
    descField,
    disabled,
  }: {
    field: string;
    tierKey?: string;
    descField: string;
    disabled?: boolean;
  }) => {
    const selected: string[] = effectivePerks(field, tierKey);
    const toggle = (key: string) => {
      const wanted = new Set(selected);
      wanted.has(key) ? wanted.delete(key) : wanted.add(key);
      // Keep canonical order from the master list.
      const next = SCHEDULING_PERKS.map((p) => p.key).filter((k) => wanted.has(k));
      // Toggling a perk both updates the saved selection AND live-updates the
      // description so checked perks appear / unchecked ones disappear.
      updateEvent({ [field]: next, [descField]: buildDesc(next) });
    };

    return (
      <div>
        <FieldLabel>What's included (checked perks appear in the description &amp; feed scheduling)</FieldLabel>
        <div className="grid grid-cols-1 gap-1.5 mt-1">
          {SCHEDULING_PERKS.map((p) => (
            <label
              key={p.key}
              className={`flex items-start gap-2 text-sm ${
                disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                disabled={disabled}
                checked={selected.includes(p.key)}
                onChange={() => !disabled && toggle(p.key)}
              />
              <span className="text-foreground">{p.label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };


  return (
    <div className="space-y-6">
      {/* Master toggle */}
      <div className="flex items-start justify-between gap-4 p-4 border border-border rounded-xl bg-card">
        <div>
          <p className="font-semibold text-foreground">Enable paid scheduling for this event</p>
          <p className="text-sm text-muted-foreground mt-1">
            When on, teams can buy priority scheduling tiers and individual
            scheduling requests in the cart. Per-request fees are configured
            in the Constraints step via each template's Fee override.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => updateEvent({ scheduling_tiers_enabled: v })}
        />
      </div>

      {enabled && (
        <SubSection title="Tier Pricing">
          <TierCard
            title="Priority Tier"
            tone="border-sky-300/50 bg-sky-500/10 dark:bg-sky-500/5"
            summary="Per-team priority upcharge"
          >
          <div>
            <FieldLabel>Priority tier name</FieldLabel>
            <input
              type="text"
              className={inputClass}
              placeholder="Priority"
              value={event.priority_tier_name ?? ""}
              onChange={(e) => updateEvent({ priority_tier_name: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Display name shown at checkout. Defaults to "Priority".
            </p>
          </div>

          <div>
            <FieldLabel>Priority tier price ($)</FieldLabel>
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputClass}
              value={event.priority_tier_price ?? 0}
              onChange={(e) => numericPatch("priority_tier_price", e.target.value, { min: 0 })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Per-team upcharge. Set to 0 to hide the Priority tier.
            </p>
          </div>

          <div>
            <FieldLabel>Priority tier description</FieldLabel>
            <Textarea
              rows={6}
              placeholder="What teams get with the Priority tier (shown at checkout)."
              value={event.priority_tier_description ?? ""}
              onChange={(e) => updateEvent({ priority_tier_description: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-filled from the checked perks below. Edit freely — checking or
              unchecking a perk rewrites this list.
            </p>
          </div>

          <PerkPicker field="priority_tier_perks" tierKey="priority" descField="priority_tier_description" />


          <div className="flex items-center justify-between border border-border rounded-lg p-3 bg-card">
            <div>
              <p className="text-sm font-medium text-foreground">Scheduling requests included free</p>
              <p className="text-xs text-muted-foreground">
                When on, teams on this tier don't pay per-request constraint fees.
              </p>
            </div>
            <Switch
              checked={event.priority_tier_waives_fees !== false}
              onCheckedChange={(v) => updateEvent({ priority_tier_waives_fees: v })}
            />
          </div>
          </TierCard>

          <TierCard
            title="Club Priority Tier"
            tone="border-emerald-300/50 bg-emerald-500/10 dark:bg-emerald-500/5"
            summary="Bundle for multiple teams from the same club"
          >
          <div>
            <FieldLabel>Club Priority tier name</FieldLabel>
            <input
              type="text"
              className={inputClass}
              placeholder="Club Priority"
              value={event.club_priority_tier_name ?? ""}
              onChange={(e) => updateEvent({ club_priority_tier_name: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Display name shown at checkout. Defaults to "Club Priority".
            </p>
          </div>

          <div>
            <FieldLabel>Club Priority tier price ($)</FieldLabel>
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputClass}
              value={event.club_priority_tier_price ?? 0}
              onChange={(e) => numericPatch("club_priority_tier_price", e.target.value, { min: 0 })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Per-team upcharge for the multi-team club bundle. Set to 0 to hide it.
            </p>
          </div>

          <div>
            <FieldLabel>Club Priority tier description</FieldLabel>
            <Textarea
              rows={6}
              placeholder="What the multi-team club bundle includes (shown at checkout)."
              value={event.club_priority_tier_description ?? ""}
              onChange={(e) => updateEvent({ club_priority_tier_description: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-filled from the checked perks below. Edit freely — checking or
              unchecking a perk rewrites this list.
            </p>
          </div>

          <PerkPicker field="club_priority_tier_perks" tierKey="club_priority" descField="club_priority_tier_description" />


          <div className="flex items-center justify-between border border-border rounded-lg p-3 bg-card">
            <div>
              <p className="text-sm font-medium text-foreground">Scheduling requests included free</p>
              <p className="text-xs text-muted-foreground">
                When on, teams on this tier don't pay per-request constraint fees.
              </p>
            </div>
            <Switch
              checked={event.club_priority_tier_waives_fees !== false}
              onCheckedChange={(v) => updateEvent({ club_priority_tier_waives_fees: v })}
            />
          </div>

          <div>
            <FieldLabel>Club Priority minimum teams</FieldLabel>
            <input
              type="number"
              min="2"
              step="1"
              className={inputClass}
              value={event.club_priority_min_teams ?? 2}
              onChange={(e) => numericPatch("club_priority_min_teams", e.target.value, { integer: true, min: 2 })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Teams from the same club required in one cart to unlock the bundle.
            </p>
          </div>
          </TierCard>

          <TierCard
            title="Showcase Tier"
            tone="border-amber-300/50 bg-amber-500/10 dark:bg-amber-500/5"
            summary="Featured court / college-coach window"
          >
          <div className="flex items-center justify-between border border-border rounded-lg p-3 bg-card">
            <div>
              <p className="text-sm font-medium text-foreground">Enable Showcase tier</p>
              <p className="text-xs text-muted-foreground">
                Featured court / college-coach window. Typically RecruitLook-branded events only.
              </p>
            </div>
            <Switch
              checked={!!event.showcase_tier_enabled}
              onCheckedChange={(v) => updateEvent({ showcase_tier_enabled: v })}
            />
          </div>

          <div>
            <FieldLabel>Showcase tier name</FieldLabel>
            <input
              type="text"
              className={inputClass}
              disabled={!event.showcase_tier_enabled}
              placeholder="Showcase"
              value={event.showcase_tier_name ?? ""}
              onChange={(e) => updateEvent({ showcase_tier_name: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Display name shown at checkout. Defaults to "Showcase".
            </p>
          </div>

          <div>
            <FieldLabel>Showcase tier price ($)</FieldLabel>
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputClass}
              disabled={!event.showcase_tier_enabled}
              value={event.showcase_tier_price ?? 0}
              onChange={(e) => numericPatch("showcase_tier_price", e.target.value, { min: 0 })}
            />
          </div>

          <div>
            <FieldLabel>Showcase tier description</FieldLabel>
            <Textarea
              rows={6}
              disabled={!event.showcase_tier_enabled}
              placeholder="What the Showcase tier includes (shown at checkout)."
              value={event.showcase_tier_description ?? ""}
              onChange={(e) => updateEvent({ showcase_tier_description: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-filled from the checked perks below. Edit freely — checking or
              unchecking a perk rewrites this list.
            </p>
          </div>

          <PerkPicker field="showcase_tier_perks" tierKey="showcase" descField="showcase_tier_description" disabled={!event.showcase_tier_enabled} />


          <div className="flex items-center justify-between border border-border rounded-lg p-3 bg-card">
            <div>
              <p className="text-sm font-medium text-foreground">Scheduling requests included free</p>
              <p className="text-xs text-muted-foreground">
                When on, teams on this tier don't pay per-request constraint fees.
              </p>
            </div>
            <Switch
              checked={event.showcase_tier_waives_fees !== false}
              disabled={!event.showcase_tier_enabled}
              onCheckedChange={(v) => updateEvent({ showcase_tier_waives_fees: v })}
            />
          </div>
          </TierCard>
        </SubSection>
      )}

      {enabled && (
        <SubSection title="Individual Scheduling Request">
          <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            Not running tiers? Leave all three tier cards above at $0 / disabled and just turn this
            on. You choose <span className="font-medium text-foreground">which request types</span>{" "}
            teams can buy in the <span className="font-medium text-foreground">Constraints</span>{" "}
            step — toggle each request template on for this event and set its Fee override there.
          </div>
          <div className="flex items-center justify-between border border-border rounded-lg p-3">
            <div>
              <p className="text-sm font-medium text-foreground">Enable individual scheduling request</p>
              <p className="text-xs text-muted-foreground">
                A one-off paid scheduling request (not a tier) teams can add in the cart.
              </p>
            </div>
            <Switch
              checked={!!event.individual_request_enabled}
              onCheckedChange={(v) => updateEvent({ individual_request_enabled: v })}
            />
          </div>

          <div>
            <FieldLabel>Individual request name</FieldLabel>
            <input
              type="text"
              className={inputClass}
              disabled={!event.individual_request_enabled}
              placeholder="Individual Scheduling Request"
              value={event.individual_request_name ?? ""}
              onChange={(e) => updateEvent({ individual_request_name: e.target.value || null })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Display name shown at checkout.
            </p>
          </div>

          <div>
            <FieldLabel>Individual request price ($)</FieldLabel>
            <input
              type="number"
              min="0"
              step="0.01"
              className={inputClass}
              disabled={!event.individual_request_enabled}
              value={event.individual_request_price ?? 0}
              onChange={(e) => numericPatch("individual_request_price", e.target.value, { min: 0 })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Per-request fee charged for a single scheduling request.
            </p>
          </div>

          <div>
            <FieldLabel>Individual request description</FieldLabel>
            <Textarea
              rows={2}
              disabled={!event.individual_request_enabled}
              placeholder="What a single scheduling request covers (shown at checkout)."
              value={event.individual_request_description ?? ""}
              onChange={(e) => updateEvent({ individual_request_description: e.target.value || null })}
            />
          </div>
        </SubSection>
      )}
    </div>
  );
}
